/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, AlertTriangle, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/constants";

// Fallback if the options endpoint is unavailable — kept in sync with
// services/analyst_tasks.py::CLOSE_OUTCOMES.
const FALLBACK_OUTCOMES = [
  { id: "done_nothing_pending", label: "Done, nothing pending", hint: "Closes the card. Nothing further is owed." },
  { id: "done_awaiting_reply", label: "Done, awaiting a reply from the client", needs: "review_date", hint: "Closes the card and records a review date on the client so it resurfaces if they go quiet." },
  { id: "done_handed_off", label: "Done, handed on to a campaign or proposal", hint: "Closes the card. The campaign or proposal now owns the follow-through." },
  { id: "needs_decision", label: "Needs a decision from someone here", needs: "owner_due", hint: "Creates an owned task with a due date, then closes the card." },
  { id: "not_worth_doing", label: "Not worth doing", hint: "Closes the card as rejected, with your reason recorded." },
  { id: "other", label: "Something else", needs: "other_text", hint: "Tell us what actually happened — we use these to learn the outcome types we haven't built yet." },
];

function deriveClientRef(sp) {
  if (!sp) return "";
  return sp.client_name || sp.company || sp.reference || sp.client_id || "";
}

function plusDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function CloseCardModal({ open, onClose, task, analystPool = [], currentUserEmail, onClosed }) {
  const [outcomes, setOutcomes] = useState(FALLBACK_OUTCOMES);
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [otherText, setOtherText] = useState("");
  const [reviewDate, setReviewDate] = useState(plusDaysISO(7));
  const [ownerId, setOwnerId] = useState("");
  const [dueDate, setDueDate] = useState(plusDaysISO(3));
  const [clientRef, setClientRef] = useState("");
  const [noClient, setNoClient] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const derived = useMemo(() => deriveClientRef(task?.source_payload), [task]);

  // Reset state each time the modal opens for a fresh card.
  useEffect(() => {
    if (!open) return;
    setOutcome("");
    setNote("");
    setOtherText("");
    setReviewDate(plusDaysISO(7));
    setDueDate(plusDaysISO(3));
    setOwnerId("");
    setClientRef(derived || "");
    setNoClient(false);
    setCandidates(null);
    setError("");
    setBusy(false);
  }, [open, derived]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/analyst-tasks/close-outcomes/options`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d?.outcomes?.length) setOutcomes(d.outcomes); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const selected = outcomes.find((o) => o.id === outcome);
  const needs = selected?.needs;

  const submit = async (overrideClientRef) => {
    setError("");
    if (!outcome) { setError("Pick an outcome — a completion needs to name what happened next."); return; }
    if (needs === "other_text" && !otherText.trim()) { setError("Tell us what happened in the box below."); return; }
    if (needs === "owner_due" && !dueDate) { setError("A decision needs a due date so it doesn't get lost."); return; }

    const effectiveClientRef = overrideClientRef !== undefined ? overrideClientRef : (clientRef || "").trim();
    const body = {
      outcome,
      note: note.trim(),
      other_text: otherText.trim(),
      client_reference: effectiveClientRef || null,
      allow_no_client: noClient || (needs !== "review_date" && !effectiveClientRef && !derived && false),
      review_date: needs === "review_date" ? reviewDate : null,
      due_date: needs === "owner_due" ? dueDate : null,
      owner_id: ownerId || null,
      owner_name: ownerId ? (analystPool.find((a) => a.user_id === ownerId)?.name || "") : null,
    };
    // If the user ticked "no client", make it explicit and drop the reference.
    if (noClient) { body.allow_no_client = true; body.client_reference = null; }

    setBusy(true);
    try {
      const r = await fetch(`${API}/analyst-tasks/${task.task_id}/close`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(d.detail || "Could not close the card. Please try again.");
        setBusy(false);
        return;
      }
      if (d.status === "client_ambiguous") {
        setCandidates(d.candidates || []);
        setError("More than one client matches. Pick the right one so the note lands on the correct record.");
        setBusy(false);
        return;
      }
      if (d.status === "client_not_found") {
        setError(`No client matches "${d.reference}". Correct the name, or tick "No client" if this card isn't about one.`);
        setBusy(false);
        return;
      }
      if (d.status === "client_required") {
        setError("This card isn't linked to a client. Name one so the note lands there, or tick \"No client\".");
        setBusy(false);
        return;
      }
      // closed
      const landed = d.client?.status === "applied";
      toast.success(
        landed
          ? `Card closed — note saved to ${d.client.client_name || "the client"}.`
          : d.scrum_task_id
            ? "Card closed — a decision task was created with a due date."
            : "Card closed."
      );
      setBusy(false);
      onClosed && onClosed(d);
    } catch (e) {
      setError("Network error — please try again.");
      setBusy(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="lens-close-modal">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Close this card
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-slate-500 -mt-1">{task.title}</p>

        <div className="space-y-4 text-sm mt-1">
          {/* Outcome — required */}
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-2">What happened? <span className="text-rose-600">*</span></p>
            <div className="space-y-1.5" data-testid="lens-close-outcomes">
              {outcomes.map((o) => (
                <label
                  key={o.id}
                  className={`flex items-start gap-2 rounded-md border p-2.5 cursor-pointer transition ${outcome === o.id ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`lens-close-outcome-${o.id}`}
                >
                  <input
                    type="radio"
                    name="close-outcome"
                    className="mt-0.5"
                    checked={outcome === o.id}
                    onChange={() => { setOutcome(o.id); setError(""); }}
                  />
                  <span>
                    <span className="font-medium text-slate-900 block leading-tight">{o.label}</span>
                    <span className="text-[11px] text-slate-500">{o.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Escape hatch free text */}
          {needs === "other_text" && (
            <div>
              <label className="text-xs font-semibold text-slate-700">In your words, what happened? <span className="text-rose-600">*</span></label>
              <textarea
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                rows={2}
                className="w-full mt-1 text-sm border border-slate-300 rounded p-2 focus:ring-1 focus:ring-emerald-400 outline-none"
                placeholder="e.g. Passed to legal for review"
                data-testid="lens-close-other-text"
              />
            </div>
          )}

          {/* Review date for awaiting-reply */}
          {needs === "review_date" && (
            <div>
              <label className="text-xs font-semibold text-slate-700">Review date (resurfaces on the client)</label>
              <input
                type="date"
                value={reviewDate}
                onChange={(e) => setReviewDate(e.target.value)}
                className="w-full mt-1 h-9 px-2 text-sm border border-slate-300 rounded"
                data-testid="lens-close-review-date"
              />
            </div>
          )}

          {/* Owner + due date for needs_decision */}
          {needs === "owner_due" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">Owner</label>
                <select
                  value={ownerId}
                  onChange={(e) => setOwnerId(e.target.value)}
                  className="w-full mt-1 h-9 px-2 text-sm border border-slate-300 rounded bg-white"
                  data-testid="lens-close-owner"
                >
                  <option value="">Unassigned</option>
                  {analystPool.map((a) => (
                    <option key={a.user_id || a.email} value={a.user_id || ""}>{a.name || a.email}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Due date <span className="text-rose-600">*</span></label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full mt-1 h-9 px-2 text-sm border border-slate-300 rounded"
                  data-testid="lens-close-due-date"
                />
              </div>
            </div>
          )}

          {/* Note — optional. Do not demand perfect data on the backlog pass. */}
          <div>
            <label className="text-xs font-semibold text-slate-700">Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="w-full mt-1 text-sm border border-slate-300 rounded p-2 focus:ring-1 focus:ring-emerald-400 outline-none"
              placeholder={'e.g. "Email sent, awaiting feedback" — or leave blank if you can\'t recall.'}
              data-testid="lens-close-note"
            />
          </div>

          {/* Client landing — the whole value of this change. */}
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-1">
              <Building2 className="h-3.5 w-3.5" /> Save this note to the client
            </p>
            <p className="text-[11px] text-slate-500 mb-2">So the next person who opens the client sees what we did and are waiting on.</p>
            <input
              type="text"
              value={clientRef}
              onChange={(e) => { setClientRef(e.target.value); setCandidates(null); }}
              disabled={noClient}
              className="w-full h-9 px-2 text-sm border border-slate-300 rounded bg-white disabled:bg-slate-100 disabled:text-slate-400"
              placeholder="Client name"
              data-testid="lens-close-client-ref"
            />
            {candidates && candidates.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="lens-close-candidates">
                <p className="text-[11px] text-slate-600">Which one?</p>
                {candidates.map((c) => (
                  <button
                    key={c.client_id}
                    onClick={() => submit(c.client_id)}
                    className="block w-full text-left text-xs px-2 py-1.5 rounded border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50"
                    data-testid={`lens-close-candidate-${c.client_id}`}
                  >
                    {c.company_name || c.client_id}
                  </button>
                ))}
              </div>
            )}
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={noClient}
                onChange={(e) => { setNoClient(e.target.checked); setError(""); }}
                data-testid="lens-close-no-client"
              />
              No client — this card isn't about one
            </label>
          </div>

          {error && (
            <div className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="lens-close-error">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="lens-close-cancel">Cancel</Button>
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => submit()}
            disabled={busy}
            data-testid="lens-close-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            Close card
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
